import { Injectable, signal, inject } from '@angular/core';
import { AuthService, User } from '@auth0/auth0-angular';
import {
  LimitedResultOfTaskGroupDto,
  MeClient,
  TaskGroupDto,
  TaskGroupsClient,
  TaskItemStatus,
} from '@r1-development/resolution-mechanicalturk-api-client';
import { firstValueFrom, filter } from 'rxjs';
import {
  GetTaskGroupsParams,
  GetMyAvailableTaskGroupsParams,
} from 'src/app/shared/models/tasks.model';
import {
  loadCursorBasedApiGenerator,
  memoize,
  setSortByDirection,
} from 'src/app/shared/utils/global.utils';
import {
  convertToGetTaskGroupsParams,
  convertToGetMyAvailableTaskGroupsParams,
} from 'src/app/shared/utils/tasks.utils';

@Injectable({
  providedIn: 'root',
})
export class SidebarApiService {
  private readonly meClient = inject(MeClient);
  private readonly taskGroupsClient = inject(TaskGroupsClient);
  private readonly authService = inject(AuthService);

  public static readonly LOAD_PAGE_SIZE = 20;
  private readonly _user = signal<User | undefined>(undefined);
  user = this._user.asReadonly();

  // Memoized version of getTaskGroupsMemoized method of the api client.
  // It saves all the responses for "maxAge" ms
  private readonly getTaskGroupsMemoized = memoize(
    this.taskGroupsClient.getTaskGroups.bind(this.taskGroupsClient),
    { maxAge: 5000 }
  );

  // Memoized version of getMyAvailableTaskGroups method of the api client.
  // It saves all the responses for "maxAge" ms
  private readonly getMyAvailableTaskGroupsMemoized = memoize(
    this.meClient.getMyAvailableTaskGroups.bind(this.meClient),
    { maxAge: 5000 }
  );

  // Makes getTaskGroups method of the client more readable by
  // placing parameters to the object with readable names
  // (instead of array). Besides that it returns Promise
  // preparing this method to work with loadCursorBasedApiGenerator
  // function.
  private readonly getTaskGroups = (params: GetTaskGroupsParams) =>
    firstValueFrom(
      this.getTaskGroupsMemoized(...convertToGetTaskGroupsParams(params))
    );

  // Pending list has predefined sort order and other parameters.
  // So this variable was created for convenience.
  private readonly getMyAvailableTaskGroups = (
    params: GetMyAvailableTaskGroupsParams
  ) =>
    firstValueFrom(
      this.getMyAvailableTaskGroupsMemoized(
        ...convertToGetMyAvailableTaskGroupsParams(params)
      )
    );

  // Waits for the authenticated user before querying so that assignedTo is
  // always populated, even when the generator is created before Auth0 emits.
  private readonly getMyOpenTaskGroups = async (
    params: GetTaskGroupsParams
  ) => {
    let userId = this._user()?.sub;
    if (!userId) {
      const user = await firstValueFrom(
        this.authService.user$.pipe(filter((u): u is User => !!u))
      );
      userId = user.sub;
    }
    return this.getTaskGroups({
      ...params,
      assignedTo: userId ? [userId] : undefined,
    });
  };

  // My Tasks static params — uses assignedTo so profile restrictions are bypassed.
  private readonly getMyTasksStaticParams = {
    status: [TaskItemStatus.Pending, TaskItemStatus.Started],
    taskStatus: [
      TaskItemStatus.Pending,
      TaskItemStatus.Started,
      TaskItemStatus.Completed,
    ],
    sort: [
      // Sort by assignedAt then dueDate, both in ascending order, to ensure that the most recently assigned and soonest due tasks are shown first.
      setSortByDirection<TaskGroupDto>('assignedAt', 'asc'),
      setSortByDirection<TaskGroupDto>('dueDate', 'asc'),
    ],
    assigned: true,
    limit: SidebarApiService.LOAD_PAGE_SIZE,
  };

  // Completed list has predefined sort order and other parameters.
  // So this variable was created for convenience.
  private readonly getCompletedTasksStaticParams = {
    status: [TaskItemStatus.Completed],
    taskStatus: [
      TaskItemStatus.Pending,
      TaskItemStatus.Started,
      TaskItemStatus.Completed,
    ],
    sort: [setSortByDirection<TaskGroupDto>('completedAt', 'desc')],
    limit: SidebarApiService.LOAD_PAGE_SIZE,
    completedBy: this._user()?.sub,
  };

  private readonly getAllTasksStaticParams = {
    status: [
      TaskItemStatus.Completed,
      TaskItemStatus.Pending,
      TaskItemStatus.Started,
    ],
    taskStatus: [
      TaskItemStatus.Completed,
      TaskItemStatus.Pending,
      TaskItemStatus.Started,
    ],
    limit: SidebarApiService.LOAD_PAGE_SIZE,
    assigned: null,
  };

  private readonly getAvailableTasksStaticParams = {
    sort: [setSortByDirection<TaskGroupDto>('dueDate', 'asc')],
    limit: SidebarApiService.LOAD_PAGE_SIZE,
  };

  constructor() {
    this.authService.user$.subscribe((user) => {
      this._user.set(user || undefined);
    });
  }

  createMyOpenTasksListGenerator(
    params: GetTaskGroupsParams = {}
  ): AsyncGenerator<LimitedResultOfTaskGroupDto> {
    return loadCursorBasedApiGenerator<
      LimitedResultOfTaskGroupDto,
      GetTaskGroupsParams
    >(this.getMyOpenTaskGroups, {
      ...this.getMyTasksStaticParams,
      ...params,
    });
  }

  createCompletedTasksListGenerator(
    params: GetTaskGroupsParams = {}
  ): AsyncGenerator<LimitedResultOfTaskGroupDto> {
    return loadCursorBasedApiGenerator<
      LimitedResultOfTaskGroupDto,
      GetTaskGroupsParams
    >(this.getTaskGroups, {
      ...this.getCompletedTasksStaticParams,
      ...params,
    });
  }

  createAllTasksListGenerator(
    params: GetTaskGroupsParams = {}
  ): AsyncGenerator<LimitedResultOfTaskGroupDto> {
    return loadCursorBasedApiGenerator<
      LimitedResultOfTaskGroupDto,
      GetTaskGroupsParams
    >(this.getTaskGroups, {
      ...this.getAllTasksStaticParams,
      ...params,
    });
  }

  createAvailableTasksListGenerator(
    params: GetMyAvailableTaskGroupsParams = {}
  ): AsyncGenerator<LimitedResultOfTaskGroupDto> {
    return loadCursorBasedApiGenerator<
      LimitedResultOfTaskGroupDto,
      GetMyAvailableTaskGroupsParams
    >(this.getMyAvailableTaskGroups, {
      ...this.getAvailableTasksStaticParams,
      ...params,
    });
  }

  clearCache(): void {
    this.getMyAvailableTaskGroupsMemoized.resetWholeCache();
    this.getTaskGroupsMemoized.resetWholeCache();
  }
}
